import React, { lazy, Suspense } from 'react';
import { Center, Spinner, Text } from '@chakra-ui/react';

/**
 * The four mutually-exclusive content states of the AI workspace.
 * Derived from `useAskAISidebar` booleans by `resolveContentState` below.
 */
export type AiContentState = 'welcome' | 'conversation' | 'streaming' | 'error';

interface ResolveContentStateInput {
  isSwitchingConversation: boolean;
  hasActiveConversation: boolean;
  isStreaming: boolean;
  createError?: string | null;
  messageError?: string | null;
}

/**
 * Pure function — state-machine mapping booleans to a single discriminant.
 * Exported for unit testing; never call `useState` here.
 */
export const resolveContentState = ({
  isSwitchingConversation,
  hasActiveConversation,
  isStreaming,
  createError,
  messageError,
}: ResolveContentStateInput): AiContentState => {
  if (createError) return 'error';
  if (messageError && !hasActiveConversation) return 'error';
  if (isSwitchingConversation) return 'conversation';
  if (!hasActiveConversation) return 'welcome';
  if (isStreaming) return 'streaming';
  return 'conversation';
};

// Lazy-load each arm — only the active one is ever mounted, so the unused
// bundles are never fetched. The Suspense boundary at this level means the
// switch costs one async chunk load on first activation, not on every render.
const WelcomeLayout = lazy(() =>
  import('./WelcomeLayout').then((m) => ({ default: m.WelcomeLayout })),
);
const ConversationLayout = lazy(() =>
  import('./ConversationLayout').then((m) => ({ default: m.ConversationLayout })),
);
const StreamingLayout = lazy(() =>
  import('./StreamingLayout').then((m) => ({ default: m.StreamingLayout })),
);
const ErrorLayout = lazy(() =>
  import('./ErrorLayout').then((m) => ({ default: m.ErrorLayout })),
);

const SwitchFallback: React.FC = () => (
  <Center flex="1" pt={12}>
    <Spinner size="sm" color="#94a3b8" />
  </Center>
);

export interface AiContentSwitchProps {
  state: AiContentState;
  /**
   * All props required by the active layout arm, forwarded verbatim.
   * Each arm is responsible for its own prop types; `any` here avoids
   * a circular union that TypeScript cannot narrow in `Suspense` wrappers.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  layoutProps: any;
}

/**
 * Renders exactly one layout arm based on `state`. Each arm is lazy-loaded
 * so unused states don't inflate the initial bundle.
 *
 * To add a new state: add its value to `AiContentState`, create a new lazy
 * import, and add one case below. No existing arm changes.
 */
export const AiContentSwitch: React.FC<AiContentSwitchProps> = ({
  state,
  layoutProps,
}) => (
  <Suspense fallback={<SwitchFallback />}>
    {state === 'welcome' && <WelcomeLayout {...layoutProps} />}
    {(state === 'conversation') && <ConversationLayout {...layoutProps} />}
    {state === 'streaming' && <StreamingLayout {...layoutProps} />}
    {state === 'error' && <ErrorLayout {...layoutProps} />}
  </Suspense>
);
